/**
 * RFQ Types admin section — CR-5466.
 *
 * Admin-managed dropdown values surfaced on the Create RFQ wizard's
 * Basic Details step. Mirrors the CommodityGroupsSection pattern
 * (per-company, enable/disable toggle, name-unique). Extracted into its
 * own file so PlatformTab.tsx doesn't grow further beyond 1000 lines.
 *
 * CR-6164: also mounted in Admin Setup → Purchasing so customer admins manage
 * their own company's list while the RFQ module is on. Platform Admin keeps
 * its copy for Helix. The backend gates writes to customer admins / Helix.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, Tag, AlertCircle } from 'lucide-react';
import { rfqAPI, fetchAllPages } from '../../../lib/api';
import type { RFQType } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';

export function RFQTypesSection() {
  const [types, setTypes] = useState<RFQType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RFQType | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', enabled: true });
  const [formError, setFormError] = useState<string | null>(null);
  // CR-6164: the target outlives the dialog's open flag so the closing
  // animation still shows the type's name rather than an empty "Delete “”?".
  const [deleteTarget, setDeleteTarget] = useState<RFQType | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // True once the server has refused for good (in use / already gone);
  // a timeout or 5xx leaves Delete enabled so the user can retry.
  const [deleteRefused, setDeleteRefused] = useState(false);

  // Refreshes keep the card mounted (no spinner swap) so open dialogs and the
  // table don't flash; only the first load and an explicit retry show it.
  const fetchData = useCallback(async () => {
    try {
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const list = await fetchAllPages<RFQType>((page) => rfqAPI.getRFQTypes({ page_size: 100, page }));
      setTypes(list);
      setLoadError(null);
    } catch (err) {
      console.error('Failed to load RFQ types', err);
      setLoadError(getApiErrorMessage(err, 'Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const retryLoad = () => {
    setLoading(true);
    fetchData();
  };

  const openDialog = (rt?: RFQType) => {
    if (rt) {
      setEditing(rt);
      setForm({ name: rt.name, enabled: rt.enabled });
    } else {
      setEditing(null);
      setForm({ name: '', enabled: true });
    }
    setFormError(null);
    setDialogOpen(true);
  };

  const openDelete = (rt: RFQType) => {
    setDeleteTarget(rt);
    setDeleteError(null);
    setDeleteRefused(false);
    setDeleteOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await rfqAPI.updateRFQType(editing.id, form);
        toast.success('RFQ Type updated');
      } else {
        await rfqAPI.createRFQType(form);
        toast.success('RFQ Type created');
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to save RFQ type', err);
      // Explain the refusal (duplicate name, module off, no access) under the
      // field, where the user is looking, and keep the dialog open to fix it.
      setFormError(getApiErrorMessage(err, 'Failed to save RFQ Type'));
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rfqAPI.deleteRFQType(deleteTarget.id);
      toast.success('RFQ Type deleted');
      setDeleteOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to delete RFQ type', err);
      // CR-6164: a type an RFQ uses is refused with a 409 that says how many
      // RFQs use it and to disable it instead. Keep that in the dialog.
      setDeleteError(getApiErrorMessage(err, 'Failed to delete RFQ Type'));
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409 || status === 404) setDeleteRefused(true);
      // 404: someone else already deleted it — drop the stale row.
      if (status === 404) await fetchData();
    } finally { setDeleting(false); }
  };

  const toggleEnabled = async (rt: RFQType) => {
    // Optimistic flip so the switch feels responsive.
    setTypes((prev) => prev.map((t) => (t.id === rt.id ? { ...t, enabled: !t.enabled } : t)));
    try {
      await rfqAPI.updateRFQType(rt.id, { enabled: !rt.enabled });
    } catch (err) {
      console.error('Failed to toggle', err);
      toast.error(getApiErrorMessage(err, 'Failed to update RFQ Type'));
      await fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Manage RFQ Types
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Admin-managed dropdown values that classify each RFQ on the Create RFQ wizard.
              </p>
            </div>
            <Button size="sm" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-1" />
              Add RFQ Type
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadError ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Couldn&rsquo;t load RFQ Types</p>
              <p className="text-sm mt-1">{loadError}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={retryLoad}>
                Try again
              </Button>
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No RFQ Types yet</p>
              <p className="text-sm mt-1">Create one to populate the Create RFQ dropdown.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={t.enabled}
                          onCheckedChange={() => toggleEnabled(t)}
                          aria-label={`Enable ${t.name}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {t.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDialog(t)} aria-label={`Edit ${t.name}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDelete(t)} aria-label={`Delete ${t.name}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit RFQ Type' : 'New RFQ Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="rfqtype-name">Name</Label>
              <Input
                id="rfqtype-name"
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setFormError(null); }}
                placeholder="e.g. Type A, Pre-qualified, Open Tender"
                aria-invalid={!!formError}
                aria-describedby={formError ? 'rfqtype-name-error' : undefined}
              />
              {formError && (
                <p id="rfqtype-name-error" className="text-sm text-destructive mt-1">{formError}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="rfqtype-enabled"
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
              <Label htmlFor="rfqtype-enabled">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete RFQ Type?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Delete &ldquo;{deleteTarget?.name}&rdquo;? This cannot be undone. If any existing RFQ references it, the
            delete will be rejected — disable it instead.
          </p>
          {deleteError && (
            <p className="text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{deleteError}</span>
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            {/* Retrying an in-use / already-gone refusal cannot succeed. */}
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting || deleteRefused}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
